/// <amd-module name="Test/SimilarResultsTab/Components/SimilarResults" />
import * as React from "react";
import { IVssComponentContext, VssComponent, Components } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { IResultDetailsPaneTabContributionContext } from "Test/common-util/Contracts/ContributedExtension.Contracts";
import * as TestManagementService from "Test/Client/TestManagementServices";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import * as Resources from "Test/SimilarResultsTab/Resources";
import { Card } from "VSSUI/Card";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import {
    ITableColumn,
    ITableRow,
    renderSimpleCell,
    Table
} from "VSSUI/Table";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { getTFSPageData } from "Tfs/Platform/Page";
import { routeUrl } from "VSS/Platform/Location";

export interface ISimilarResultState {
    loading: boolean;
    errorMessage?: string;
}

export interface ISimilarResultsData {
    testRunId: string;
    testResultId: string;
    url: string;
}

export class SimilarResultDetailsTabComponent extends VssComponent<IResultDetailsPaneTabContributionContext, ISimilarResultState> {
    private _pageContext: IVssPageContext;
    private _tcmService: TestManagementService.ITestManagementService;
    private _results: ObservableArray<ISimilarResultsData>;
    constructor(props: IResultDetailsPaneTabContributionContext, context: IVssComponentContext) {
        super(props, context);
        this._pageContext = this.context.pageContext;
        this._tcmService = this._pageContext.getService<TestManagementService.ITestManagementService>(TestManagementService.TestManagementServiceName);
        this.state = { loading: true };
    }

    public componentDidMount(): void {
        this._invokeSimilarTestResults();
    }

    public render(): JSX.Element {
        if (!this.props.resultId) {
            return this._getMessageBarComponent(Resources.FeatureNotAvailable, MessageCardSeverity.Info);
        }

        if (this.state.loading) {
            return this._getLoadingView();
        }

        if (this.state.errorMessage) {
            return this._getMessageBarComponent(this.state.errorMessage, MessageCardSeverity.Error);
        }

        if (this._results.length == 0) {
            return this._getMessageBarComponent(Resources.NoSimilarResultsFound, MessageCardSeverity.Info);
        }

        return (
            <Card className="flex-grow bolt-table-card" >
                <Table 
                        columns={this._tableColumns} 
                        itemProvider={this._results} 
                        showLines={true}
                        scrollable={true}
                        onActivate={this._onActivateRow}
                />
            </Card>
        );
    }

    private _getLoadingView(): JSX.Element {
        return <Spinner
            ariaLabel={Resources.LoadingSimilarResultsMessage}
            className="flex-grow"
            size={SpinnerSize.large}
            label={Resources.LoadingSimilarResultsMessage}
        />
    }

    private _getMessageBarComponent(text: string, severity: MessageCardSeverity): JSX.Element {
        return <MessageCard
            className="flex-self-stretch flex-grow"
            severity={severity}>
            {text}
        </MessageCard>
    }

    private async _invokeSimilarTestResults() {
        if (this.props.resultId) {
            try {
                const results = await this._tcmService.getSimilarTestResults(this.props.runId, this.props.resultId, 0);
                const data = results.map<ISimilarResultsData>((result: TCMContracts.TestCaseResult) => {
                    let dataUrl : string = "";
                    let runId: string = result.testRun.id;
                    let resultId: string = result.id.toString();
                    if(result.buildReference && result.buildReference.id > 0) {
                        dataUrl = this._getBuildUrl(result.buildReference.id.toString(), runId, resultId);
                    }

                    if(result.releaseReference && result.releaseReference.id > 0){
                        dataUrl = this._getReleaseUrl(result.releaseReference.id.toString(), 
                                                      result.releaseReference.environmentId.toString(), runId, resultId);
                    }

                    return {
                        testResultId: resultId,
                        testRunId: runId,
                        url: dataUrl
                    } as ISimilarResultsData
                })

                this._results = new ObservableArray(data);
                this.setState({
                    loading: false
                });
            }
            catch (error) {
                this.setState({
                    loading: false,
                    errorMessage: error.message || error
                });
            }

        }
    }

    private _tableColumns: ITableColumn<any>[] = [
        {
            id: "testRunId",
            width: new ObservableValue(300),
            name: Resources.TestRunId,
            renderCell: renderSimpleCell
        },
        {
            id: "testResultId",
            width: new ObservableValue(300),
            name: Resources.TestResultId,
            renderCell: renderSimpleCell
        }
    ];

    private _getBuildUrl(buildId: string, runId: string, resultId: string) {
        const newBuildHubRoute = "ms.vss-build-web.ci-results-hub-route";
        const tfsProjectData = getTFSPageData(this.context.pageContext);

        const routeValues: { [key: string]: string } = {};
        routeValues["buildId"] = buildId;
        routeValues["project"] = tfsProjectData && tfsProjectData.project ? tfsProjectData.project.name : "";
        routeValues["view"] = "ms.vss-test-web.build-test-results-tab";
        routeValues["runId"] = runId;
        routeValues["resultId"] = resultId;
        routeValues["paneView"] = "debug";
        return routeUrl(this.context.pageContext, newBuildHubRoute, routeValues);
    }

    private _getReleaseUrl(releaseId: string, environmentId: string, runId: string, resultId: string) {
        const newReleaseHubRoute = "ms.vss-releaseManagement-web.cd-release-progress-default-route";
        const tfsProjectData = getTFSPageData(this.context.pageContext);

        const routeValues: { [key: string]: string } = {};
        routeValues["releaseId"] = releaseId;
        routeValues["environmentId"] = environmentId;
        routeValues["project"] = tfsProjectData && tfsProjectData.project ? tfsProjectData.project.name : "";
        routeValues["_a"] = "release-environment-extension";
        routeValues["extensionId"] = "ms.vss-test-web.test-result-in-release-environment-editor-tab";
        routeValues["runId"] = runId;
        routeValues["resultId"] = resultId;
        return routeUrl(this.context.pageContext, newReleaseHubRoute, routeValues);
    }

    private _onActivateRow = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: ITableRow<any>) => {
        //there seems to be framework issue on clickFPS where url parameters are getting removed hence using window.open for time being
        window.open(row.data.url,'_blank');
    }
}

Components.add("similarResultDetailsTab", SimilarResultDetailsTabComponent);
